import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardActionArea, CardContent, Stack, Typography,
} from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import StorageRoundedIcon from '@mui/icons-material/StorageRounded';
import VpnKeyRoundedIcon from '@mui/icons-material/VpnKeyRounded';
import { useAuth } from '../App';
import { apiRequest } from '../api';

const asRecords = (value: unknown): Record<string, unknown>[] => Array.isArray(value)
  ? value.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object')
  : [];

const payloadRecords = (value: unknown, key: string): Record<string, unknown>[] => {
  if (Array.isArray(value)) return asRecords(value);
  if (value && typeof value === 'object') return asRecords((value as Record<string, unknown>)[key]);
  return [];
};

const formatCurrency = (value: number) => value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

const tiles = [
  {
    title: 'Rechnungen',
    description: 'Sehen Sie Ihre Rechnungen und Buchungen ein.',
    href: '/rechnungen',
    icon: ReceiptLongRoundedIcon,
    color: '#4277fc',
    showBalance: true,
  },
  {
    title: 'DNS Verwaltung',
    description: 'Verwalten Sie Ihre DNS-Zonen und passen Sie Einträge zentral an.',
    href: 'https://cloud.caix.de',
    icon: LanguageRoundedIcon,
    color: '#7c4dff',
  },
  {
    title: 'Domain Verwaltung',
    description: 'Verwalten Sie Ihre Domains und behalten Sie Ihre Domain-Services im Blick.',
    href: 'https://partner.routing.net/',
    icon: StorageRoundedIcon,
    color: '#00897b',
  },
  {
    title: 'Tunnel Service',
    description: 'Veröffentlichen Sie Webseiten oder verbinden Sie sich mit SSH-Servern hinter NAT oder einer Firewall.',
    href: 'https://cloud.caix.de',
    icon: VpnKeyRoundedIcon,
    color: '#ed6c02',
  },
];

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [balance, setBalance] = React.useState<number | null>(null);
  const [balanceLoading, setBalanceLoading] = React.useState(true);
  const [balanceError, setBalanceError] = React.useState(false);

  React.useEffect(() => {
    if (!token) return undefined;
    let active = true;
    setBalanceLoading(true);
    setBalanceError(false);

    Promise.all([
      apiRequest<unknown>('/account/invoices', token),
      apiRequest<unknown>('/account/invoices/positions', token),
      apiRequest<unknown>('/account/invoices/adjustments', token),
    ]).then(([invoicePayload, positionPayload, adjustmentPayload]) => {
      const invoiceIDs = new Set(payloadRecords(invoicePayload, 'invoices').map((invoice) => Number(invoice.id_invoice) || 0));
      const invoiceTotal = asRecords(positionPayload).reduce((total, position) => {
        const invoiceID = Number(position.id_invoice) || 0;
        const amount = Number(position.amount) || 0;
        return invoiceIDs.has(invoiceID) && amount >= 0 ? total + amount : total;
      }, 0);
      const adjustmentTotal = payloadRecords(adjustmentPayload, 'adjustments').reduce((total, adjustment) => total + (Number(adjustment.amount) || 0), 0);
      if (active) setBalance(invoiceTotal + adjustmentTotal);
    }).catch(() => {
      if (active) setBalanceError(true);
    }).finally(() => {
      if (active) setBalanceLoading(false);
    });

    return () => { active = false; };
  }, [token]);

  return (
    <Box
      sx={{
        display: 'grid',
        width: '100%',
        minHeight: { xs: 'auto', sm: 'calc(100vh - 160px)' },
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
        gridTemplateRows: { xs: 'none', sm: 'repeat(2, minmax(200px, 1fr))' },
        gap: { xs: 2, sm: 3 },
      }}
    >
      {tiles.map(({ title, description, href, icon: Icon, color, showBalance }) => {
        const external = href.startsWith('http');
        return (
          <Card
            key={title}
            elevation={0}
            sx={{
              display: 'flex',
              minHeight: 200,
              border: '1px solid',
              borderColor: 'grey.200',
              borderRadius: 3,
              transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: '0 12px 32px rgba(31, 55, 88, 0.12)',
                borderColor: color,
              },
            }}
          >
            <CardActionArea
              onClick={() => {
                if (external) window.open(href, '_blank', 'noopener,noreferrer');
                else navigate(href);
              }}
              sx={{ display: 'flex', alignItems: 'stretch', flexGrow: 1, p: { xs: 2.5, sm: 4 } }}
            >
              <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', p: 0, '&:last-child': { pb: 0 }, width: '100%' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%', mb: 3 }}>
                  <Box sx={{ display: 'grid', placeItems: 'center', width: 56, height: 56, borderRadius: 2, bgcolor: `${color}14`, color }}>
                    <Icon fontSize="large" />
                  </Box>
                  <ArrowForwardRoundedIcon sx={{ color: 'text.secondary' }} />
                </Stack>
                <Typography variant="h5" component="h2" fontWeight={700} gutterBottom>
                  {title}
                </Typography>
                <Typography color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  {description}
                </Typography>
                {showBalance && (
                  <Typography sx={{ mt: 1.5, color, fontWeight: 700 }}>
                    {balanceLoading
                      ? 'Offener Betrag wird geladen …'
                      : balanceError || balance === null
                        ? 'Offener Betrag derzeit nicht verfügbar'
                        : `${balance < 0 ? 'Guthaben' : 'Aktuell offener Betrag'}: ${formatCurrency(Math.abs(balance))}`}
                  </Typography>
                )}
              </CardContent>
            </CardActionArea>
          </Card>
        );
      })}
    </Box>
  );
};

export default DashboardPage;
